/* eslint-disable complexity */
import cloneDeep from 'lodash/cloneDeep';

import { Integration } from '@futrli-sdk/app-store';
import { DataSource, Organisation } from '@futrli-sdk/organisations';
import { OrganisationListItem, WorkspacesListingItem } from '@futrli-sdk/workspaces';

import { PREDICT_PRODUCT_ID } from '../../app-core/config';

export { PREDICT_PRODUCT_ID } from '../../app-core/config';

/**
 * Transforms an OrganisationListItem into the shared Organisation interface.
 * @param o organisation to be transformed
 * @param workspaceId workspace id
 * @param integrations list of integrations
 * @returns organisation of type Organisation
 */
export const getTransformedOrgData = (
  o: OrganisationListItem,
  workspaceId: string,
  integrations: Integration[]
): Organisation =>
  ({
    ...o,
    created: '',
    modified: '',
    image: '',
    workspace_id: workspaceId,
    syncing_enabled: true,
    enabled_products: [],
    datasource: {
      // ...o.datasource,
      id: o.datasource?.id || '',
      integration_id: o.datasource?.integration_id || '',
      status: {
        value: o?.datasource?.sync_status || '',
        description: '',
        modified: o?.datasource?.sync_status_modified || '',
        created: ''
      },
      parent_datasource_id: o?.datasource?.id || '',
      image: integrations.find(i => i.id === o?.datasource?.integration_id)?.image || '',
      type: '',
      name: '',
      currency_code: '',
      sync_history: []
    }
  } as Organisation);

/**
 * Finds specific organisation within list of workspaces and updates it's status to given sync status
 * @param initialWorkspaces initial list of workspaces from Redux
 * @param workspaceId current workspace id
 * @param organisationId current org id
 * @returns
 */
export const updateWorkspacesOrgWithSyncStatus = (
  initialWorkspaces: WorkspacesListingItem[] | null,
  workspaceId: string,
  organisationId: string,
  datasource: DataSource
) => {
  if (!initialWorkspaces) {
    return null;
  }

  const workspaces = cloneDeep(initialWorkspaces);

  workspaces.forEach(w => {
    if (w.id === workspaceId) {
      const updatedWorkspace = {
        ...w
      };
      const updatedOrgIndex = updatedWorkspace.organisations.findIndex(o => o.id === organisationId);
      if (typeof updatedOrgIndex === 'number') {
        updatedWorkspace.organisations[updatedOrgIndex] = {
          ...updatedWorkspace.organisations[updatedOrgIndex],
          datasource: {
            sync_status: datasource.status.value,
            sync_status_modified: datasource.status.modified,
            integration_id: datasource.integration_id,
            id: datasource.id
          }
        };
      }
      return { ...updatedWorkspace };
    }
    return {
      ...w
    };
  });

  return workspaces;
};

/**
 * Updates specific workspace within workspaces array with new organisations
 * @param workspaceId current workspace id
 * @param initialWorkspaces workspaces array
 * @param organisations organisations array
 * @returns updated workspaces array
 */
export const getUpdatedWorkspacesOrgs = (
  workspaceId: string,
  initialWorkspaces: WorkspacesListingItem[] | null,
  organisations: Organisation[] | null
): WorkspacesListingItem[] | null => {
  if (!initialWorkspaces) {
    return null;
  }

  const workspaces = cloneDeep(initialWorkspaces);

  const newWokspaces = workspaces.map(w => {
    if (w.id === workspaceId) {
      return {
        ...w,
        organisations: organisations ? organisations.map(o => getTransformedOrgToOrganisationListItem(o)) : []
      };
    }
    return {
      ...w
    };
  });

  return newWokspaces;
};

/**
 * Transforms organisation of type Organisation to organisation of type OrganisationListItem
 * @param o organisation of type Organisation
 * @returns organisation of type OrganisationListItem
 */
export const getTransformedOrgToOrganisationListItem = (o: Organisation): OrganisationListItem => {
  return {
    id: o.id,
    name: o.name,
    legacy_deactivated_at: o.legacy_deactivated_at,
    datasource: {
      id: o.datasource.id,
      sync_status: o.datasource.status.value,
      sync_status_modified: o.datasource.status.modified,
      integration_id: o.datasource.integration_id
    },
    predict_product_id: o.enabled_products.find(pr => pr.product_id === PREDICT_PRODUCT_ID)?.product_id || '',
    sxp_product_instance_id: null,
    sxp_business_id: null,
    sxp_futrli_tenant_id: null,
    can_be_accessed: o.can_be_accessed,
    billed_by: o.billed_by,
    billed_by_status: o.billed_by_status,

    // WARNING - the below properties dont exist on standard org data and so these properties are probably incorrect!
    is_holding: false,
    is_sage_for_accountants: false,
    is_externally_managed: false,
    display_global_nav: false
  } as OrganisationListItem;
};

/**
 * Returns updated workspaces ready for Redux
 * @param workspaces Existing list of workspaces
 * @param activeWorkspace Currently opened workspace (workspace which settings were updated)
 * @param newWorkspaceName Newly entered workspace name
 * @param isDefaultWorkspace Flag for determining if workspace is selected as default one
 * @returns Transformed workspaces after submitting workspace PATCH request (ready for Redux)
 */
export const getUpdatedWorkspacesAfterSubmittingSettings = (
  workspaces: WorkspacesListingItem[],
  activeWorkspace: WorkspacesListingItem,
  newWorkspaceName: string,
  isDefaultWorkspace: boolean
): WorkspacesListingItem[] => {
  // Find workspace that already has is_default_workspace (if any)
  const existingDefaultWorkspace = workspaces.find(w => w.member.is_default_workspace);

  // Update workspaces for storing in Redux. Active workspace is updated with name (new or existing)
  // and with isDefaultWorkspace value
  let updatedWorkspaces = workspaces.map(w =>
    w.id === activeWorkspace.id
      ? {
          ...w,
          name: newWorkspaceName || activeWorkspace.name,
          member: {
            ...w.member,
            is_default_workspace: isDefaultWorkspace
          }
        }
      : { ...w }
  );

  // We don't have existing workspace with is_default_workspace flag set
  if (!existingDefaultWorkspace) {
    return updatedWorkspaces;
  }

  // If we have existing workspace with is_default_workspace flag set and
  // that workspace is not the same as workspace that's being edited and isDefaultWorkspace is set to true,
  // frontend needs to toggle off is_default_workspace for that workspace because there can be only one workspace
  // with is_default_workspace set to true
  if (existingDefaultWorkspace.id !== activeWorkspace.id && isDefaultWorkspace) {
    updatedWorkspaces = updatedWorkspaces.map(w => {
      if (w.id === existingDefaultWorkspace.id) {
        return {
          ...w,
          member: {
            ...w.member,
            is_default_workspace: false
          }
        };
      }
      return {
        ...w
      };
    });
  }

  return updatedWorkspaces;
};

/**
 * checks if a workspace is pure SFA workspace
 * @param workspace
 * @returns boolean
 */
export const isPureSFAWorkspace = (workspace: WorkspacesListingItem) => {
  return (
    workspace.organisations.length > 0 &&
    workspace.organisations.every(org => org.kind === 'sage_non_practice' || org.kind === 'sage_practice')
  );
};
